import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"

// Connects to data-controller="qr-scanner"
export default class extends Controller {
  static targets = ["scanButton", "stopButton", "cameraSelect", "cameraSelectionBlock", "scannerContainer"]
  static values = { nominationUrl: String }

  connect() {
    this.scanner = null
    this.isScanning = false
    this.availableCameras = []
    this.selectedCameraId = null
    this.loadSavedCamera()
    this.loadAvailableCameras()
  }

  disconnect() {
    this.stopScan()
  }

  /**
   * Загружает список доступных камер и отображает селект
   */
  async loadAvailableCameras() {
    try {
      this.availableCameras = await Html5Qrcode.getCameras()
      this.updateCameraSelect()
    } catch (error) {
      console.error("Failed to get cameras:", error)
      this.hideCameraSelection()
    }
  }

  /**
   * Обновляет селект камер на основе доступных устройств
   */
  updateCameraSelect() {
    if (!this.hasCameraSelectTarget || this.availableCameras.length === 0) {
      this.hideCameraSelection()
      return
    }

    // Показываем блок выбора камеры
    this.cameraSelectionBlockTarget.style.display = 'block'

    // Очищаем опции кроме первой (placeholder)
    const select = this.cameraSelectTarget
    while (select.options.length > 1) {
      select.remove(1)
    }

    // Добавляем камеры в селект
    this.availableCameras.forEach((camera, index) => {
      const option = document.createElement('option')
      option.value = camera.id
      option.textContent = camera.label || `Camera ${index + 1}`
      select.appendChild(option)
    })

    // Если есть сохраненная камера и она все еще доступна, выбираем её
    if (this.selectedCameraId && this.availableCameras.some(cam => cam.id === this.selectedCameraId)) {
      select.value = this.selectedCameraId
    } else if (this.availableCameras.length > 0) {
      // Иначе выбираем первую камеру
      select.value = this.availableCameras[0].id
      this.selectedCameraId = this.availableCameras[0].id
    }
  }

  /**
   * Скрывает блок выбора камеры (если нет камер)
   */
  hideCameraSelection() {
    if (this.hasCameraSelectionBlockTarget) {
      this.cameraSelectionBlockTarget.style.display = 'none'
    }
  }

  /**
   * Обработчик изменения выбранной камеры
   */
  async handleCameraChange(event) {
    const selectedId = event.target.value
    if (!selectedId) return

    this.selectedCameraId = selectedId
    this.saveCameraId()

    // Если сканирование активно, перезапускаем сканер с новой камерой
    if (this.isScanning) {
      await this.stopScan()
      await this.startScan()
    }
  }

  /**
   * Сохраняет ID выбранной камеры в localStorage
   */
  saveCameraId() {
    if (this.selectedCameraId) {
      localStorage.setItem('selectedQrScannerCameraId', this.selectedCameraId)
    }
  }

  /**
   * Загружает сохраненную камеру из localStorage
   */
  loadSavedCamera() {
    const saved = localStorage.getItem('selectedQrScannerCameraId')
    if (saved) {
      this.selectedCameraId = saved
    }
  }

  /**
   * Запускает сканер камеры
   */
  async startScan() {
    if (this.isScanning) return

    try {
      // Очищаем контейнер перед инициализацией
      this.scannerContainerTarget.innerHTML = ''

      const scannerId = `qr-reader-${Date.now()}`
      const scannerDiv = document.createElement('div')
      scannerDiv.id = scannerId
      this.scannerContainerTarget.appendChild(scannerDiv)

      this.scanner = new Html5Qrcode(scannerId)
      
      const config = {
        fps: 10,
        // aspectRatio: 1.0
      }

      // Определяем какую камеру использовать
      const cameraConstraints = this.selectedCameraId 
        ? { deviceId: { exact: this.selectedCameraId } }
        : { facingMode: "environment" }

      await this.scanner.start(
        cameraConstraints,
        config,
        this.onScanSuccess.bind(this),
        this.onScanFailure.bind(this)
      )

      this.isScanning = true
      this.updateButtons()
    } catch (error) {
      console.error("Failed to start QR scanner:", error)
      this.showError("Не удалось запустить сканер. Проверьте разрешения для камеры.")
    }
  }

  /**
   * Останавливает сканер камеры
   */
  async stopScan() {
    if (this.scanner && this.isScanning) {
      try {
        await this.scanner.stop()
        this.scanner = null
        this.isScanning = false
        this.updateButtons()
      } catch (error) {
        console.error("Failed to stop QR scanner:", error)
      }
    }
    
    // Очищаем контейнер
    if (this.hasScannerContainerTarget) {
      this.scannerContainerTarget.innerHTML = ''
    }
  }

  onScanSuccess(decodedText, decodedResult) {
    console.log(`QR Code scanned: ${decodedText}`, decodedResult)
    
    // Stop scanning
    this.stopScan()
    
    // Redirect to the same page with scanned code
    const url = new URL(this.nominationUrlValue, window.location.origin)
    url.searchParams.set('scanned_code', decodedText)
    window.location.href = url.toString()
  }

  onScanFailure(error) {
    // Do nothing, scanning continues
    // console.warn(`QR Code scan error: ${error}`)
  }

  updateButtons() {
    if (this.hasScanButtonTarget && this.hasStopButtonTarget) {
      if (this.isScanning) {
        this.scanButtonTarget.classList.add('d-none')
        this.stopButtonTarget.classList.remove('d-none')
      } else {
        this.scanButtonTarget.classList.remove('d-none')
        this.stopButtonTarget.classList.add('d-none')
      }
    }
  }

  showError(message) {
    // Create and show error alert
    const alert = document.createElement('div')
    alert.className = 'alert alert-danger alert-dismissible fade show mt-3'
    alert.innerHTML = `
      <i class="fa-solid fa-exclamation-triangle me-2"></i>
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    this.scannerContainerTarget.parentNode.insertBefore(alert, this.scannerContainerTarget.nextSibling)
  }
}
